import { FunctionComponent } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { useTranslator } from "../../hooks/i18n.ts";
import { requestGet } from "../../utils/request.ts";

interface StatData {
    date: string;
    avg_person: number;
    avg_vehicle: number;
}

export const AIActivityChart: FunctionComponent<{ cameraId?: number; days?: number }> = ({ cameraId, days = 7 }) => {
    const chartRef = useRef<HTMLCanvasElement>(null);
    const chartInstance = useRef<any>(null);
    const [data, setData] = useState<StatData[]>([]);
    const t = useTranslator();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const params: any = { days };
                if (cameraId) params.camera_id = cameraId;
                const resp = await requestGet<{ stats: StatData[] }>("/../ai/stats", params);
                setData(resp.stats);
            } catch (e) {
                console.error("Failed to fetch stats", e);
            }
        };
        fetchData();
    }, [cameraId, days]);

    useEffect(() => {
        if (!chartRef.current || data.length === 0) return;

        let active = true;

        const loadAndRender = async () => {
            try {
                // Dynamic import for Chart.js
                const { Chart, registerables } = await import("chart.js");
                
                if (!active) return;

                // Register components if not already registered
                // Note: Chart.register is idempotent enough or we can catch error
                try {
                    Chart.register(...registerables);
                } catch (e) {
                    // Ignore registration errors
                }

                if (chartInstance.current) {
                    chartInstance.current.destroy();
                }

                const ctx = chartRef.current?.getContext("2d");
                if (!ctx) return;

                chartInstance.current = new Chart(ctx, {
                    type: 'line',
                    data: {
                        labels: data.map(d => d.date),
                        datasets: [
                            {
                                label: t({ zh: '平均人数', en: 'Avg People' }),
                                data: data.map(d => d.avg_person),
                                borderColor: 'rgb(255, 193, 7)',
                                backgroundColor: 'rgba(255, 193, 7, 0.5)',
                                tension: 0.1
                            },
                            {
                                label: t({ zh: '平均车辆', en: 'Avg Vehicles' }),
                                data: data.map(d => d.avg_vehicle),
                                borderColor: 'rgb(23, 162, 184)',
                                backgroundColor: 'rgba(23, 162, 184, 0.5)',
                                tension: 0.1
                            }
                        ]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            y: {
                                beginAtZero: true
                            }
                        }
                    }
                });
            } catch (e) {
                console.error("Failed to load or render chart", e);
            }
        };

        loadAndRender();

        return () => {
            active = false;
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [data, t]);

    if (data.length === 0) return null;

    const title = t({
        zh: `AI 活跃度趋势 (${days === 365 ? "近1年" : `近${days}天`})`,
        en: `AI Activity Trend (${days === 365 ? "1 Year" : `${days} Days`})`,
    });

    return (
        <div style={{ height: "300px", width: "100%", padding: "1rem", backgroundColor: "var(--pico-card-background-color)", borderRadius: "var(--pico-border-radius)", marginBottom: "1rem" }}>
            <h5 style={{ marginBottom: "1rem" }}>{title}</h5>
            <div style={{ position: "relative", height: "220px" }}>
                <canvas ref={chartRef} />
            </div>
        </div>
    );
};
